import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { Button, Icon } from 'semantic-ui-react';
import { usePopup } from '../../lib/popup';

import DroppableTypes from '../../constants/DroppableTypes';
import CardContainer from '../../containers/CardContainer';
import NameEdit from './NameEdit';
import CardAdd from './CardAdd';
import ActionsStep from './ActionsStep';
import { ReactComponent as PlusMathIcon } from '../../assets/images/plus-math-icon.svg';
import { formatPoints } from '../../utils/formatPoints';

import styles from './List.module.scss';

const List = React.memo(
  ({
    id,
    index,
    name,
    isPersisted,
    cardIds,
    boardId,
    projectId,
    allProjectsToLists,
    canEdit,
    isSuperuser,
    isMonthlyReportAvailable,
    isMonthClosed,
    pointsSum,
    closedPointsSumExcludingOtherMonth,
    hasMonthSet,
    shouldAutoCloseMonth,
    shouldAutoResetMonth,
    reportYear,
    reportMonth,
    onUpdate,
    onMove,
    onTransfer,
    onDelete,
    onArchive,
    onBoardFetch,
    onMonthlyReportOpen,
    onCardCreate,
    onCompleteMonth,
    onResetMonth,
  }) => {
    const [t] = useTranslation();
    const [isAddCardOpened, setIsAddCardOpened] = useState(false);
    const autoResetRequestedRef = useRef(false);

    const nameEdit = useRef(null);
    const listWrapper = useRef(null);

    // Автоматически закрываем месяц, если все условия выполнены
    useEffect(() => {
      console.log('[List] Auto-close check:', {
        shouldAutoCloseMonth,
        isMonthClosed,
        hasOnCompleteMonth: !!onCompleteMonth,
        listId: id,
      });
      if (canEdit && shouldAutoCloseMonth && !isMonthClosed && onCompleteMonth) {
        console.log('[List] Auto-closing month for list:', id);
        onCompleteMonth();
      }
    }, [canEdit, shouldAutoCloseMonth, isMonthClosed, onCompleteMonth, id]);

    // Автоматически сбрасываем закрытие месяца, если в списке есть незакрытые задачи или задачи закрыты в другом месяце
    useEffect(() => {
      if (!isMonthClosed) {
        autoResetRequestedRef.current = false;
        return;
      }

      if (!canEdit || !onResetMonth || !hasMonthSet || !reportYear || !reportMonth) {
        return;
      }

      if (!shouldAutoResetMonth) {
        return;
      }

      if (autoResetRequestedRef.current) {
        return;
      }

      autoResetRequestedRef.current = true;
      console.log('[List] Auto-reset month close for list:', id, { reportYear, reportMonth });
      onResetMonth(reportYear, reportMonth);
    }, [
      canEdit,
      isMonthClosed,
      hasMonthSet,
      reportYear,
      reportMonth,
      shouldAutoResetMonth,
      onResetMonth,
      id,
    ]);

    const handleHeaderClick = useCallback(() => {
      if (isPersisted && canEdit) {
        nameEdit.current.open();
      }
    }, [isPersisted, canEdit]);

    const handleNameUpdate = useCallback(
      (newName) => {
        onUpdate({
          name: newName,
        });
      },
      [onUpdate],
    );

    const handleAddCardClick = useCallback(() => {
      setIsAddCardOpened(true);
    }, []);

    const handleAddCardClose = useCallback(() => {
      setIsAddCardOpened(false);
    }, []);

    const handleNameEdit = useCallback(() => {
      nameEdit.current.open();
    }, []);

    const handleCardAdd = useCallback(() => {
      setIsAddCardOpened(true);
    }, []);

    const handlePointsSumClick = useCallback(
      (e) => {
        e.stopPropagation();
        if (isMonthlyReportAvailable) {
          onMonthlyReportOpen();
        }
      },
      [isMonthlyReportAvailable, onMonthlyReportOpen],
    );

    useEffect(() => {
      if (isAddCardOpened) {
        listWrapper.current.scrollTop = listWrapper.current.scrollHeight;
      }
    }, [cardIds, isAddCardOpened]);

    const ActionsPopup = usePopup(ActionsStep);
    const isActionsAvailable = isPersisted && (canEdit || isMonthlyReportAvailable);

    const listPeriodText = useMemo(() => {
      if (!hasMonthSet || !reportYear || !reportMonth) {
        return null;
      }

      const prefix = t('List_headerPeriod', { defaultValue: 'Calculation plan: ' });
      const period = t('format:listMonthYear', {
        defaultValue: 'yyyy LLLL',
        postProcess: 'formatDate',
        value: new Date(reportYear, reportMonth - 1, 1),
      });

      return `${prefix}${period}`;
    }, [hasMonthSet, reportYear, reportMonth, t]);

    const cardsNode = (
      <Droppable
        droppableId={`list:${id}`}
        type={DroppableTypes.CARD}
        isDropDisabled={!isPersisted}
      >
        {({ innerRef, droppableProps, placeholder }) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <div {...droppableProps} ref={innerRef}>
            <div className={styles.cards}>
              {cardIds.map((cardId, cardIndex) => (
                <CardContainer key={cardId} id={cardId} index={cardIndex} />
              ))}
              {placeholder}
              {canEdit && (
                <CardAdd
                  isOpened={isAddCardOpened}
                  onCreate={onCardCreate}
                  onClose={handleAddCardClose}
                />
              )}
            </div>
          </div>
        )}
      </Droppable>
    );

    return (
      <Draggable draggableId={`list:${id}`} index={index} isDragDisabled={!isPersisted || !canEdit}>
        {({ innerRef, draggableProps, dragHandleProps }) => (
          <div
            {...draggableProps} // eslint-disable-line react/jsx-props-no-spreading
            data-drag-scroller
            ref={innerRef}
            className={styles.innerWrapper}
          >
            <div className={styles.outerWrapper}>
              {isMonthlyReportAvailable && pointsSum !== 0 && (
                  <div className={styles.pointsSum} onClick={handlePointsSumClick}>
                    Баллов: {hasMonthSet && closedPointsSumExcludingOtherMonth > 0
                      ? (
                          <>
                            {formatPoints(pointsSum)}/<span className={styles.pointsSumGreen}>{formatPoints(closedPointsSumExcludingOtherMonth)}</span>
                          </>
                        )
                      : formatPoints(pointsSum)}
                  </div>
              )}
              {isMonthClosed && (
                <div className={styles.monthClosedBanner}>
                  Отчет за месяц закрыт
                </div>
              )}
              <div
                {...dragHandleProps} // eslint-disable-line react/jsx-props-no-spreading
                className={classNames(styles.header, canEdit && styles.headerEditable)}
                onClick={handleHeaderClick}
              >
                
                <NameEdit ref={nameEdit} defaultValue={name} onUpdate={handleNameUpdate}>
                  <div className={styles.headerName}>
                    <div className={styles.headerTitle}>{name}</div>
                    {listPeriodText && <div className={styles.headerPeriod}>{listPeriodText}</div>}
                  </div>
                </NameEdit>
                {isActionsAvailable && (
                  <ActionsPopup
                    list={{ id, boardId, projectId }}
                    projectsToLists={allProjectsToLists}
                    onNameEdit={handleNameEdit}
                    onCardAdd={handleCardAdd}
                    onMove={onMove}
                    onTransfer={onTransfer}
                    onDelete={onDelete}
                    onArchive={onArchive}
                    onBoardFetch={onBoardFetch}
                    canEdit={canEdit}
                    isSuperuser={isSuperuser}
                    isMonthlyReportAvailable={isMonthlyReportAvailable}
                    onMonthlyReportOpen={onMonthlyReportOpen}
                  >
                    <Button className={classNames(styles.headerButton, styles.target)}>
                      <Icon fitted name="pencil" size="small" />
                    </Button>
                  </ActionsPopup>
                )}
              </div>
              <div
                ref={listWrapper}
                className={classNames(
                  styles.cardsInnerWrapper,
                  (isAddCardOpened || !canEdit) && styles.cardsInnerWrapperFull,
                )}
              >
                <div className={styles.cardsOuterWrapper}>{cardsNode}</div>
              </div>
              {!isAddCardOpened && canEdit && (
                <button
                  type="button"
                  disabled={!isPersisted}
                  className={classNames(styles.addCardButton)}
                  onClick={handleAddCardClick}
                >
                  <PlusMathIcon className={styles.addCardButtonIcon} />
                  <span className={styles.addCardButtonText}>
                    {cardIds.length > 0 ? t('action.addAnotherCard') : t('action.addCard')}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}
      </Draggable>
    );
  },
);

List.propTypes = {
  id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  isPersisted: PropTypes.bool.isRequired,
  cardIds: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  boardId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  allProjectsToLists: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  canEdit: PropTypes.bool.isRequired,
  isSuperuser: PropTypes.bool,
  isMonthlyReportAvailable: PropTypes.bool.isRequired,
  isMonthClosed: PropTypes.bool,
  pointsSum: PropTypes.number,
  closedPointsSumExcludingOtherMonth: PropTypes.number,
  hasMonthSet: PropTypes.bool,
  shouldAutoCloseMonth: PropTypes.bool,
  shouldAutoResetMonth: PropTypes.bool,
  reportYear: PropTypes.number,
  reportMonth: PropTypes.number,
  onUpdate: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onBoardFetch: PropTypes.func.isRequired,
  onMonthlyReportOpen: PropTypes.func.isRequired,
  onCardCreate: PropTypes.func.isRequired,
  onCompleteMonth: PropTypes.func,
  onResetMonth: PropTypes.func,
};

export default List;
